import { useEffect, useMemo, useState } from 'react';
import { Search, CheckSquare, Square, Copy, Loader } from 'lucide-react';
import { createDataSyncRun, DataSyncRun } from '../../../utils/dataSyncApi';
import { useStandardIngredientsStore } from '../../../store/useStandardIngredientsStore';
import { useAuthReady } from '../../../hooks/useAuthReady';
import { SyncProgressBar } from './SyncProgressBar';

interface IngredientsSyncTabProps {
  activeRun: DataSyncRun | null;
  onRunStarted: (runId: string) => void;
  runDisabled: boolean;
}

export function IngredientsSyncTab({
  activeRun,
  onRunStarted,
  runDisabled,
}: IngredientsSyncTabProps) {
  const authReady = useAuthReady(true);
  const { ingredients, isLoading, fetchIngredients } = useStandardIngredientsStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authReady) return;
    fetchIngredients();
  }, [authReady, fetchIngredients]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return ingredients.filter(
      (ing) =>
        (ing.name || '').toLowerCase().includes(term) ||
        (ing.id || '').toLowerCase().includes(term) ||
        (ing.aliases || []).some((a) => a.toLowerCase().includes(term))
    );
  }, [ingredients, searchTerm]);

  const toggle = (id: string) => {
    setSelectAll(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectionCount = selectAll ? ingredients.length : selected.size;

  const handleCopy = async () => {
    if (selectionCount === 0) return;
    if (
      !window.confirm(
        `Copier ${selectAll ? 'TOUS' : selectionCount} ingrédient(s) vers la préprod ? Les documents existants seront écrasés.`
      )
    ) {
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const { runId } = await createDataSyncRun({
        type: 'ingredients',
        ids: selectAll ? 'all' : [...selected],
      });
      onRunStarted(runId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setStarting(false);
    }
  };

  if (isLoading && ingredients.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-menioo-green" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SyncProgressBar run={activeRun} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:border-menioo-green focus:ring-menioo-green"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setSelectAll(false);
            setSelected(new Set(filtered.map((i) => i.id)));
          }}
          className="text-sm text-menioo-navy hover:underline"
        >
          Sélectionner filtrés ({filtered.length})
        </button>
        <button
          type="button"
          onClick={() => {
            setSelectAll(true);
            setSelected(new Set());
          }}
          className="text-sm text-menioo-navy hover:underline"
        >
          Tout ({ingredients.length})
        </button>
        <button
          type="button"
          onClick={() => {
            setSelectAll(false);
            setSelected(new Set());
          }}
          className="text-sm text-gray-500 hover:underline"
        >
          Effacer
        </button>
        <button
          type="button"
          onClick={handleCopy}
          disabled={runDisabled || starting || selectionCount === 0}
          className="ml-auto inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
        >
          {starting ? (
            <Loader className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Copy className="h-4 w-4 mr-2" />
          )}
          Copier ({selectAll ? 'tout' : selectionCount})
        </button>
      </div>

      {selectAll && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Mode « tout » : tous les ingrédients standards de la base source seront copiés.
        </p>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg divide-y max-h-[28rem] overflow-y-auto">
        {filtered.map((ing) => {
          const isSelected = selectAll || selected.has(ing.id);
          return (
            <button
              key={ing.id}
              type="button"
              onClick={() => toggle(ing.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
            >
              {isSelected ? (
                <CheckSquare className="h-4 w-4 text-menioo-green flex-shrink-0" />
              ) : (
                <Square className="h-4 w-4 text-gray-400 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-menioo-navy truncate">{ing.name}</div>
                <div className="text-xs text-gray-400 truncate">
                  {ing.id}
                  {ing.category ? ` · ${ing.category}` : ''}
                </div>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-gray-500">Aucun ingrédient</p>
        )}
      </div>
    </div>
  );
}
